import { useTranslation } from 'react-i18next';
import type { HowItWorksStep } from '@/mocks/howItWorks';

interface StepCardProps {
  step: HowItWorksStep;
  index: number;
}

export default function StepCard({ step, index }: StepCardProps) {
  const { t } = useTranslation('howItWorks');
  const reversed = index % 2 === 1;

  return (
    <article
      id={`step-${step.number}`}
      className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-center"
    >
      {/* Image */}
      <div className={`relative overflow-hidden rounded-xl ${reversed ? 'lg:order-2' : ''}`}>
        <img
          src={step.image}
          alt={t(step.titleKey)}
          title={`${t(step.titleKey)} - ${t('step.titleSuffix')}`}
          className="w-full h-[260px] md:h-[340px] object-cover object-center"
        />
        <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-sm">
          <span className="text-ink-700 font-bold text-lg">{step.number}</span>
        </div>
      </div>

      {/* Content */}
      <div className={`${reversed ? 'lg:order-1' : ''}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-warm-50 flex items-center justify-center">
            <i className={`${step.icon} text-warm-500 text-xl md:text-2xl`} />
          </div>
          <div>
            <span className="block text-xs font-semibold text-warm-500 uppercase tracking-[0.15em]">
              {t('step.label')} {step.number}
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-ink-700 leading-tight">
              {t(step.titleKey)}
            </h3>
          </div>
        </div>

        <p className="text-sm md:text-base text-ink-500 leading-relaxed mb-5">
          {t(step.descKey)}
        </p>

        {/* Note callout */}
        <div className="flex items-start gap-3 p-4 bg-warm-50 border-l-4 border-warm-500 rounded-r-lg mb-5">
          <div className="w-6 h-6 shrink-0 rounded-full bg-warm-500 flex items-center justify-center mt-0.5">
            <i className="ri-information-line text-white text-xs" />
          </div>
          <p className="text-sm text-ink-600 leading-relaxed">{t(step.noteKey)}</p>
        </div>

        {/* Items */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
          {step.itemKeys.map((key) => (
            <li key={key} className="flex items-start gap-2.5">
              <div className="w-5 h-5 shrink-0 rounded-full bg-cream-100 flex items-center justify-center mt-0.5">
                <i className="ri-check-line text-warm-500 text-xs" />
              </div>
              <span className="text-sm text-ink-500 leading-relaxed">{t(key)}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}